import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import moment from "moment";
import {
  ChecklistGroup,
  Comment,
  Role,
  RoleAccess,
  Row,
  StatusKey,
} from "./types";
import { ROLES } from "./mockData";
import { useAgendaApi } from "./agendaApi";
import { useFetchContext } from "../../../common/FetchContext";
import { useMutation } from "react-query";

interface ConfirmPayload {
  title: string;
  body: string;
  label: string;
  onConfirm: () => void;
}

interface State {
  role: Role;
  query: string;
  openRowId: number | null;
  activeAnchor: string | null;
  editing: string | null;
  editErr: string | null;
  composing: boolean;
  doneOpen: boolean;
  checklist: ChecklistGroup[];
  comments: Comment[];
  rows: Row[];
  confirm: ConfirmPayload | null;
  lastSavedFlash: number;
  rolesAccess: RoleAccess[];
}

type Action =
  | { type: "SET_ROLE"; role: Role }
  | { type: "SET_QUERY"; query: string }
  | { type: "OPEN_REPORT"; id: number }
  | { type: "TO_GRID" }
  | { type: "SET_ANCHOR"; anchor: string | null }
  | { type: "TOGGLE_DONE_ITEM"; id: string }
  | { type: "TOGGLE_PENDING"; id: string }
  | { type: "EDIT_NOTE"; id: string | null }
  | { type: "CANCEL_NOTE" }
  | { type: "SAVE_NOTE"; id: string; note: string }
  | { type: "SET_NOTE_ERR"; id: string | null }
  | { type: "ACK_ITEM"; id: string }
  | { type: "ACK_COMMENT"; id: string }
  | { type: "START_COMMENT" }
  | { type: "CANCEL_COMMENT" }
  | {
      type: "SAVE_COMMENT";
      stage: "verify" | "approval";
      anchor: string;
      text: string;
      by: string;
    }
  | { type: "TRANSITION"; patch: Partial<Row> & { id: number } }
  | { type: "RELEASE_HELD"; stage: "verify" | "approval" }
  | { type: "TOGGLE_DONE_SECTION" }
  | { type: "SHOW_CONFIRM"; payload: ConfirmPayload }
  | { type: "CLOSE_CONFIRM" }
  | { type: "SET_ROWS"; rows: Row[] }
  | { type: "SET_ROLES_ACCESS"; rolesAccess: RoleAccess[] }
  | { type: "FLASH_SAVED" };

const initialState: State = {
  role: "writer",
  query: "",
  openRowId: null,
  activeAnchor: null,
  editing: null,
  editErr: null,
  composing: false,
  doneOpen: true,
  checklist: [],
  comments: [],
  rows: [],
  confirm: null,
  lastSavedFlash: 0,
  rolesAccess: [],
};

function mapItem(
  checklist: ChecklistGroup[],
  id: string,
  fn: (i: ChecklistGroup["items"][number]) => ChecklistGroup["items"][number],
): ChecklistGroup[] {
  return checklist.map((g) => ({
    ...g,
    items: g.items.map((i) => (i.id === id ? fn(i) : i)),
  }));
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_ROLE":
      return { ...state, role: action.role, query: "" };
    case "SET_QUERY":
      return { ...state, query: action.query };
    case "OPEN_REPORT": {
      const rows = state.rows.map((r) =>
        r.id === action.id && !r.start
          ? { ...r, start: new Date().toISOString().slice(0, 10) }
          : r,
      );
      return {
        ...state,
        rows,
        openRowId: action.id,
        activeAnchor: null,
        editing: null,
        editErr: null,
        composing: false,
      };
    }
    case "TO_GRID":
      return {
        ...state,
        openRowId: null,
        activeAnchor: null,
        editing: null,
        editErr: null,
        composing: false,
      };
    case "SET_ANCHOR":
      return { ...state, activeAnchor: action.anchor };
    case "TOGGLE_DONE_ITEM": {
      const checklist = mapItem(state.checklist, action.id, (i) => ({
        ...i,
        s: i.s === "d" ? "n" : "d",
      }));
      return {
        ...state,
        checklist,
        editing: state.editing === action.id ? null : state.editing,
        lastSavedFlash: Date.now(),
      };
    }
    case "TOGGLE_PENDING": {
      let editing = state.editing;
      const checklist = mapItem(state.checklist, action.id, (i) => {
        if (i.s === "p") {
          editing = state.editing === action.id ? null : state.editing;
          return { ...i, s: "n" };
        }
        if (!i.note) editing = action.id;
        return { ...i, s: "p" };
      });
      return {
        ...state,
        checklist,
        editing,
        editErr: null,
        lastSavedFlash: Date.now(),
      };
    }
    case "EDIT_NOTE":
      return { ...state, editing: action.id, editErr: null };
    case "CANCEL_NOTE":
      return { ...state, editing: null, editErr: null };
    case "SAVE_NOTE": {
      const checklist = mapItem(state.checklist, action.id, (i) => ({
        ...i,
        note: action.note,
      }));
      return {
        ...state,
        checklist,
        editing: null,
        editErr: null,
        lastSavedFlash: Date.now(),
      };
    }
    case "SET_NOTE_ERR":
      return { ...state, editErr: action.id };
    case "ACK_ITEM": {
      const checklist = mapItem(state.checklist, action.id, (i) => ({
        ...i,
        ack: true,
      }));
      return { ...state, checklist, lastSavedFlash: Date.now() };
    }
    case "ACK_COMMENT": {
      const comments = state.comments.map((c) =>
        c.id === action.id ? { ...c, ack: true } : c,
      );
      return { ...state, comments };
    }
    case "START_COMMENT":
      return { ...state, composing: true };
    case "CANCEL_COMMENT":
      return { ...state, composing: false };
    case "SAVE_COMMENT": {
      const c: Comment = {
        id: "m" + Date.now(),
        stage: action.stage,
        by: action.by,
        at: moment().format("DD-MM-YYYY HH:mm"),
        anchor: action.anchor,
        text: action.text,
        ack: false,
        held: true,
      };
      return {
        ...state,
        comments: [...state.comments, c],
        composing: false,
      };
    }
    case "RELEASE_HELD": {
      const comments = state.comments.map((c) =>
        c.stage === action.stage ? { ...c, held: false } : c,
      );
      return { ...state, comments };
    }
    case "TRANSITION": {
      const rows = state.rows.map((r) =>
        r.id === action.patch.id ? { ...r, ...action.patch } : r,
      );
      return { ...state, rows, openRowId: null };
    }
    case "TOGGLE_DONE_SECTION":
      return { ...state, doneOpen: !state.doneOpen };
    case "SHOW_CONFIRM":
      return { ...state, confirm: action.payload };
    case "CLOSE_CONFIRM":
      return { ...state, confirm: null };
    case "FLASH_SAVED":
      return { ...state, lastSavedFlash: Date.now() };
    case "SET_ROWS":
      return {
        ...state,
        rows: action.rows,
      };
    case "SET_ROLES_ACCESS": {
      const rolesAccess = action.rolesAccess;
      const current = rolesAccess.find((r) => r.role === state.role);
      const nextRole =
        current && current.access
          ? state.role
          : (rolesAccess.find((r) => r.access)?.role ?? state.role);
      return {
        ...state,
        rolesAccess,
        role: nextRole,
        query: nextRole === state.role ? state.query : "",
      };
    }
    default:
      return state;
  }
}

export function useAgendaState() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [completedRows, setCompletedRows] = useState<Row[]>([]);
  const { getAgendaByRole, getRolesAccess, getCompletedAgenda } =
    useAgendaApi();

  useEffect(() => {
    let cancelled = false;

    const loadRolesAccess = async () => {
      try {
        const response = await getRolesAccess();
        if (!cancelled) {
          dispatch({ type: "SET_ROLES_ACCESS", rolesAccess: response.data });
        }
      } catch (err) {
        if (!cancelled) {
          dispatch({ type: "SET_ROLES_ACCESS", rolesAccess: [] });
        }
        console.error("Failed to load roles access", err);
      }
    };

    loadRolesAccess();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAgenda = async () => {
      setIsLoading(true);
      try {
        const response = await getAgendaByRole(state.role);
        if (!cancelled) {
          dispatch({ type: "SET_ROWS", rows: response.data });
        }
      } catch (err) {
        if (!cancelled) {
          dispatch({ type: "SET_ROWS", rows: [] });
        }
        console.error("Failed to load agenda for role", state.role, err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadAgenda();

    return () => {
      cancelled = true;
    };
  }, [state.role]);
  useEffect(() => {
    let cancelled = false;

    const loadCompletedAgenda = async () => {
      try {
        const response = await getCompletedAgenda();

        if (!cancelled) {
          setCompletedRows(response.data);
        }
      } catch (error) {
        console.error("Failed to load completed reports", error);

        if (!cancelled) {
          setCompletedRows([]);
        }
      }
    };

    loadCompletedAgenda();

    return () => {
      cancelled = true;
    };
  }, []);

  const me = useCallback(() => state.role, [state.role]);

  const openRow = useMemo(
    () =>
      state.openRowId == null
        ? null
        : (state.rows.find((r) => r.id === state.openRowId) ?? null),
    [state.openRowId, state.rows],
  );

  const ckItems = useMemo(
    () => state.checklist.flatMap((g) => g.items),
    [state.checklist],
  );
  const ckPending = useMemo(
    () => ckItems.filter((i) => i.s === "p"),
    [ckItems],
  );
  const ckAcked = useMemo(() => ckPending.filter((i) => i.ack), [ckPending]);
  const ckDoneCount = useMemo(
    () => ckItems.filter((i) => i.s === "d").length,
    [ckItems],
  );

  const cmFor = useCallback(
    (st: StatusKey | "verify" | "approval") => {
      const s =
        st === "verify" || st === "corr_ver"
          ? "verify"
          : st === "approval" || st === "corr_app"
            ? "approval"
            : null;
      return s ? state.comments.filter((c) => c.stage === s) : [];
    },
    [state.comments],
  );
  const cmSent = useCallback(
    (st: StatusKey | "verify" | "approval") => cmFor(st).filter((c) => !c.held),
    [cmFor],
  );
  const cmAck = useCallback(
    (st: StatusKey | "verify" | "approval") => cmSent(st).filter((c) => c.ack),
    [cmSent],
  );
  const cmHeld = useCallback(
    (st: StatusKey | "verify" | "approval") => cmFor(st).filter((c) => c.held),
    [cmFor],
  );

  const { authAxios } = useFetchContext();

  const acknowledgeChecklistMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await authAxios.post("", {
        checklistItemId: id,
        acknowledged: true,
      });

      return response.data;
    },
  });

  const acknowledgeCommentMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await authAxios.post(
        "https://jsonplaceholder.typicode.com/posts",
        {
          commentId: id,
          acknowledged: true,
        },
      );

      return response.data;
    },
  });

  const acknowledgeChecklistItem = async (id: string): Promise<void> => {
    await acknowledgeChecklistMutation.mutateAsync(id);
  };

  const acknowledgeComment = async (id: string): Promise<void> => {
    await acknowledgeCommentMutation.mutateAsync(id);
  };

  return {
    state,
    dispatch,
    me,
    openRow,
    ckItems,
    ckPending,
    ckAcked,
    ckDoneCount,
    cmFor,
    cmSent,
    cmAck,
    cmHeld,
    acknowledgeChecklistItem,
    acknowledgeComment,
    isLoading,
    completedRows,
  };
}

export type AgendaController = ReturnType<typeof useAgendaState>;

/**
 * Row queue filters — mirror the QUEUE object in the HTML (lines 566–572).
 * The verifier sees a read-only copy while the approver holds the report.
 */
export const copyFor = (r: Row) => ["approval", "corr_app"].includes(r.st);

export function rowsForRole(rows: Row[], role: Role, query: string): Row[] {
  const q = query.toLowerCase();

  const filtered = rows.filter((r) => {
    if (role === "writer") {
      return [
        "notstarted",
        "writing",
        "corr_rev",
        "corr_ver",
        "corr_app",
        "with_reviewer",
        "with_verifier",
        "with_approver",
      ].includes(r.st);
    }

    if (role === "reviewer") {
      return ["review", "with_writer", "corr_rev"].includes(r.st);
    }

    if (role === "verifier") {
      return ["verify", "with_writer", "corr_ver"].includes(r.st) || copyFor(r);
    }

    if (role === "approver") {
      return ["approval", "with_writer", "corr_app"].includes(r.st);
    }

    return false;
  });

  return q
    ? filtered.filter((r) =>
        `${r.proj} ${r.cust} ${r.bom}`.toLowerCase().includes(q),
      )
    : filtered;
}
export const doneRows = (rows: Row[]) =>
  rows.filter((r) => r.st === "approved");
